import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import EmptyState from "@/components/shared/EmptyState";
import SeverityBadge from "@/components/shared/SeverityBadge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import api, { extractApiError } from "@/lib/api";
import { filterUnrespondedByOrg } from "@/lib/incidentScope";
import { Bell, Check, MapPin, Clock, Car, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { downloadReport } from "@/lib/downloadReport";
import { useIncidentSocket } from "@/hooks/useIncidentSocket";
import LocationLabel from "@/components/shared/LocationLabel";

function normalizeCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function formatDateTime(value) {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString();
}

function resolveCoords(location) {
  if (!location || typeof location !== "object") return null;
  const lat = Number.parseFloat(location.lat ?? location.latitude);
  const lon = Number.parseFloat(location.lon ?? location.lng ?? location.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lon)) return [lat, lon];
  return null;
}

function normalizeIncident(item) {
  const primaryVehicle = Array.isArray(item?.vehicles)
    ? item.vehicles.find((v) => v?.role === "PRIMARY") || item.vehicles[0]
    : null;

  const locationText =
    typeof item?.location === "string"
      ? item.location
      : item?.location?.address || item?.address || item?.place || "—";

  return {
    id: item?.id || item?.incident_id || "",
    type: item?.incident_type || item?.type || "Incident",
    severity: String(item?.severity || "low"),
    vehicle: primaryVehicle?.registration_number || primaryVehicle?.vehicle_id || item?.vehicle || item?.plate || "—",
    driver: item?.driver?.name || item?.driver?.user_id || item?.driver || "—",
    location: locationText,
    locationRaw: item?.location ?? item?.address ?? item?.place ?? null,
    coords: resolveCoords(item?.location),
    description: item?.description || "",
    time: item?.event_time || item?.created_at || item?.updated_at || item?.timestamp || "",
    status: String(item?.status || "OPEN").toUpperCase(),
    raw: item,
  };
}

function playAlertTone() {
  if (typeof window === "undefined" || !window.AudioContext) return;
  try {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = "sine";
    osc.frequency.value = 900;
    gain.gain.value = 0.08;
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start();
    setTimeout(() => {
      osc.stop();
      audioCtx.close();
    }, 180);
  } catch {
    // ignore audio failures
  }
}

export default function PoliceNewAlerts() {
  const { can } = useAuth();
  const canUpdateIncidents = can("INCIDENTS.UPDATE");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ackLoadingId, setAckLoadingId] = useState("");
  const [error, setError] = useState("");
  const [liveCount, setLiveCount] = useState(0);

  const fetchOpenAlerts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/incidents", {
        params: { status: "OPEN", page: 1, page_size: 20 },
      });
      const normalized = filterUnrespondedByOrg(normalizeCollection(data))
        .map(normalizeIncident)
        .filter((x) => x.id);
      setList(normalized);
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message || "Failed to load open alerts.");
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOpenAlerts();
  }, [fetchOpenAlerts]);

  useIncidentSocket((incidentData) => {
    const incoming = normalizeIncident(incidentData);
    if (!incoming.id || incoming.status !== "OPEN") return;

    setList((prev) => {
      const exists = prev.some((x) => x.id === incoming.id);
      if (exists) {
        return prev.map((x) => (x.id === incoming.id ? incoming : x));
      }
      playAlertTone();
      setLiveCount((n) => n + 1);
      return [incoming, ...prev].slice(0, 20);
    });
  });

  const handleDownload = () => {
    downloadReport({
      filename: "police_new_alerts",
      title: "Police - New Alerts Report",
      headers: ["Type", "Severity", "Vehicle", "Driver", "Location", "Time"],
      rows: list.map((i) => [i.type, i.severity, i.vehicle, i.driver, i.location, formatDateTime(i.time)]),
    });
    toast.success("Report downloaded successfully");
  };

  const acknowledge = async (incidentId) => {
    if (!incidentId) return;
    setAckLoadingId(incidentId);
    try {
      await api.post(`/incidents/${incidentId}/service-response`, { status: "ACKNOWLEDGED" });
      setList((prev) => prev.filter((x) => x.id !== incidentId));
      toast.success("Incident acknowledged.");
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to acknowledge incident.");
    } finally {
      setAckLoadingId("");
    }
  };

  const subtitle = useMemo(() => {
    if (loading) return "Loading open incidents...";
    return `Open incidents awaiting police acknowledgment. Live +${liveCount}`;
  }, [loading, liveCount]);

  return (
    <div className="space-y-6" data-testid="police-new-alerts">
      <PageHeader
        title="New Alerts"
        subtitle={subtitle}
        actions={(
          <Button variant="outline" onClick={fetchOpenAlerts} className="gap-2" disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </Button>
        )}
        onDownload={handleDownload}
      />

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {loading && list.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-sm text-slate-500 flex items-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading alerts...
        </div>
      ) : list.length === 0 ? (
        <EmptyState title="No new alerts" description="You're all caught up. New emergencies will appear here." icon={Bell} />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {list.map((i) => (
            <div key={i.id} className="rounded-xl border border-slate-100 bg-white p-5 shadow-[0_2px_10px_-3px_rgba(15,23,42,0.05)]" data-testid={`alert-${i.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-display text-lg font-bold text-slate-900">{i.type}</p>
                </div>
                <SeverityBadge severity={i.severity} />
              </div>

              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-700">
                  <Car size={14} className="text-slate-400" />
                  <span className="font-mono text-xs">{i.vehicle}</span>
                  <span className="text-slate-400">·</span>
                  <span>{i.driver}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <MapPin size={14} className="text-slate-400" />
                  <LocationLabel locationValue={i.locationRaw ?? i.location} />
                </div>
                <div className="flex items-center gap-2 text-slate-500">
                  <Clock size={14} />
                  <span className="text-xs">{formatDateTime(i.time)}</span>
                </div>
              </div>

              {i.description ? <p className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">{i.description}</p> : null}

              {canUpdateIncidents ? (
                <div className="mt-4 flex gap-2">
                  <Button
                    className="flex-1 bg-[hsl(var(--role-primary))] text-white hover:bg-[hsl(var(--role-primary))]/90"
                    onClick={() => acknowledge(i.id)}
                    disabled={ackLoadingId === i.id}
                    data-testid={`ack-${i.id}`}
                  >
                    {ackLoadingId === i.id ? (
                      <><Loader2 size={14} className="mr-1.5 animate-spin" /> Acknowledging...</>
                    ) : (
                      <><Check size={14} className="mr-1.5" /> Acknowledge</>
                    )}
                  </Button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
